import { useEffect, useState } from 'react'
import styled from 'styled-components'
import { getAddress, isAddress } from 'viem'
import { getTokenLogoURLByAddress } from 'utils/getTokenLogoURL'
import { DISPLAY } from '../ui'
import { useListLogo } from './useListLogos'

const Wrap = styled.span<{ $size: number; $ring?: boolean | 'soft' }>`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: none;
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  border-radius: 50%;
  overflow: hidden;
  background: radial-gradient(120% 120% at 30% 20%, #2a3a86 0%, #150f4a 60%, #0a0e22 100%);
  ${({ $ring }) => ($ring === 'soft' ? 'box-shadow: 0 0 0 2px rgba(223, 233, 255, 0.22), 0 0 16px 1px rgba(159, 182, 255, 0.3);' : $ring ? 'box-shadow: 0 0 0 2px rgba(223, 233, 255, 0.4), 0 0 26px 4px rgba(159, 182, 255, 0.35), 0 0 70px 14px rgba(126, 0, 254, 0.16);' : 'box-shadow: inset 0 0 0 1px rgba(180, 200, 255, 0.22);')}
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`
const Mono = styled.span<{ $size: number }>`
  font-family: ${DISPLAY};
  font-weight: 800;
  font-size: ${({ $size }) => Math.round($size * 0.42)}px;
  line-height: 1;
  color: #dfe9ff;
  text-transform: uppercase;
`

/**
 * A token's logo: the approved project logo, then the logo GeckoTerminal knows, then the wallet-assets logo, then a monogram.
 * Each source that fails to load is skipped, so a broken image never shows.
 */
export default function TokenLogo({ srcs, address, symbol, size = 48, ring }: { srcs?: Array<string | null | undefined>; address?: string; symbol?: string | null; size?: number; ring?: boolean | 'soft' }) {
  // the app's own logo map (CADINU, CBON) is keyed by the checksummed address; the API speaks lowercase
  const listed = useListLogo(address)
  const list = [...(srcs ?? []), listed, address && isAddress(address) ? getTokenLogoURLByAddress(getAddress(address), 56) : null].filter((s): s is string => Boolean(s))
  const key = list.join('|')
  const [i, setI] = useState(0)
  useEffect(() => setI(0), [key])
  const src = list[i]
  return (
    <Wrap $size={size} $ring={ring} role="img" aria-label={`${symbol ?? 'Token'} logo`}>
      {src ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img src={src} alt="" width={size} height={size} loading="lazy" decoding="async" referrerPolicy="no-referrer" onError={() => setI((n) => n + 1)} />
      ) : (
        <Mono $size={size}>{(symbol ?? '?').replace(/[^\p{L}\p{N}]/gu, '').slice(0, 1) || '?'}</Mono>
      )}
    </Wrap>
  )
}
